import React from 'react';
import tw from 'twin.macro';
import clsx from 'clsx';
import {
  bgColors,
  borderColors,
  getColorHexOrFallback,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import { Color } from '@teetsh/app/src/types/colors';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';
import { darken } from '@teetsh/app/src/utils/style/darken';
import { BadgeColor } from '@teetsh/app/src/types/colors';
import styled from '@emotion/styled';
import { Icon } from '@teetsh/app/src/components/Icons/icon';
import { XMarkIcon } from '@heroicons/react/20/solid';

interface Props {
  id?: string;
  children?: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  outlined?: boolean;
  empty?: boolean;
  color?: Color | TagColorWithIntensity | BadgeColor;
  interactive?: boolean;
  active?: boolean;
  icon?: Icon;
  rightIcon?: Icon;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  onClose?: () => void;
  'data-testid'?: string;
}

function convertBadgeColor(
  color: BadgeColor | Color | TagColorWithIntensity,
  outlined?: boolean,
): Color | TagColorWithIntensity | undefined {
  // Outlined style
  if (outlined) {
    switch (color) {
      case 'red':
        return 'framboise-intense';
      case 'blue':
        return 'bleu-ciel-intense';
      case 'gray':
        return undefined;
      case 'green':
        return 'aloe-vera-intense';
      case 'pink':
        return 'barbapapa-intense';
      case 'yellow':
        return 'limoncello-intense';
      case 'indigo':
        return 'lavande-intense';
      default:
        return color;
    }
  }
  // Filled style
  switch (color) {
    case 'red':
      return 'framboise-quiet';
    case 'blue':
      return 'bleu-ciel-quiet';
    case 'gray':
      return undefined;
    case 'green':
      return 'aloe-vera-quiet';
    case 'pink':
      return 'barbapapa-quiet';
    case 'yellow':
      return 'limoncello-quiet';
    case 'indigo':
      return 'lavande-quiet';
    default:
      return color;
  }
}

const StyledIcon = styled('div')(() => [tw`size-4 shrink-0`]);

export function intentToColorMapping(
  intent:
    | 'success'
    | 'success-quiet'
    | 'warning'
    | 'danger'
    | 'danger-quiet'
    | 'primary',
): TagColorWithIntensity {
  switch (intent) {
    case 'success':
      return 'aloe-vera-intense';
    case 'success-quiet':
      return 'aloe-vera-quiet';
    case 'warning':
      return 'miel-intense';
    case 'danger':
      return 'framboise-intense';
    case 'danger-quiet':
      return 'framboise-quiet';
    case 'primary':
      return 'bleu-ciel-intense';
    default:
      return 'gris-bleu-intense';
  }
}

export function Tag(props: Props) {
  const {
    children,
    className,
    size = 'md',
    outlined,
    empty,
    color: rawColor,
    interactive = !!props.onClick,
    active,
    icon,
    rightIcon,
    onClose,
    ...rest
  } = props;
  const color = rawColor ? convertBadgeColor(rawColor, outlined) : undefined;
  const filled = !outlined && !empty;
  const activeStyles = [
    tw`border-neutral-intense`,
    color && `border-color: ${darken(0.2, getColorHexOrFallback(color))};`,
  ];
  return (
    <span
      className={clsx(className, 'tsh-tag')}
      css={[
        tw`inline-flex gap-x-1 items-center rounded-lg font-medium text-center text-balance text-neutral-quiet border border-transparent`,
        size === 'sm' && tw`px-2 py-0.5 text-sm leading-4`,
        size === 'md' && tw`px-2 py-1 text-sm leading-4`,
        size === 'lg' && tw`px-3 py-1 text-base leading-[18px]`,
        filled && [
          tw`bg-neutral-quiet`,
          color && [bgColors(color), getTextColorFromBackgroundColor(color)],
        ],
        outlined &&
          !empty && [
            tw`text-neutral-intense border-neutral-intense`,
            color && borderColors(color),
            (color === 'white' || color === 'gris-supershy') &&
              tw`border-neutral-quiet`,
          ],
        empty && [
          tw`text-neutral-moderate border-dashed border-neutral-moderate`,
        ],
        interactive && [
          tw`cursor-pointer hover:bg-neutral-quiet`,
          filled && [
            tw`hover:bg-neutral-moderate`,
            color &&
              `&:hover {
                background-color: ${darken(0.1, getColorHexOrFallback(color))};
              }`,
          ],
        ],
        active && activeStyles,
        // TODO: Appliquer systématiquement via :active ? Ou seulement si "interactive" et/ou onClose ?
        (interactive || onClose) && {
          '&:active': activeStyles,
        },
        // TODO: ajouter directement ces cas au type Color + fonctions utilitaires concernées ?
      ]}
      {...rest}
    >
      {icon && <StyledIcon as={icon as any} />}
      {children}
      {rightIcon && <StyledIcon as={rightIcon as any} />}
      {onClose && (
        <button
          type="button"
          tw="size-4 shrink-0 rounded-full inline-flex"
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
        >
          <XMarkIcon />
        </button>
      )}
    </span>
  );
}
